// src/components/onboarding/OnboardingOverlay.tsx
//
// H-2C2 Phase 2.B step 2.B — onboarding overlay component.
//
// Thin orchestration shell:
//   1. Gate on `VITE_ONBOARDING_OVERLAY === 'true'` (build-time flag). When
//      unset / 'false' / anything else, the component returns null
//      immediately — no hook calls, no Modal mount, zero DOM impact. The
//      default for both staging and production deploys.
//   2. When the flag is on, consult `useOnboardingOverlay()` for the
//      predicate-derived `isOpen` flag (Step 1: PR #30). Predicate mirrors
//      the three render-time skip clauses in OnboardingWizardPage.
//   3. Render `<Modal>` wrapping `<OnboardingWizardSteps>` (Step 2.A:
//      PR #31). Steps owns all wizard internals: handleFinish, idempotency
//      ref, Fix A persist, telemetry, demo CTA, step rendering.
//
// HARD INVARIANTS (asserted by source-level guards in the test file):
//   - This component MUST NOT import `useNavigate` from `react-router-dom`.
//   - This component MUST NOT contain a `navigate(` call.
//   Onboarding overlay state is pure UI (open / close); routing decisions
//   live exclusively inside OnboardingWizardSteps (which uses navigate for
//   the same paths the standalone wizard always has).
//
// MOUNT POINT:
//   <AppLayout> mounts <OnboardingOverlayProvider><OnboardingOverlay/>
//   </OnboardingOverlayProvider> as a sibling of <CRMSuiteShell /> /
//   legacy layout + <CommandPalette />. Because /onboarding lives OUTSIDE
//   <AppLayout> (verified: AppRouter.tsx:90-103), there is no scenario in
//   which both the standalone wizard and the overlay mount simultaneously
//   — no duplicate-wizard, no duplicate-telemetry.

import Modal from '../ui/Modal';
import { useOnboardingOverlay } from '../../hooks/useOnboardingOverlay';
import OnboardingWizardSteps from './OnboardingWizardSteps';

/**
 * Build-time gate. Reads `import.meta.env.VITE_ONBOARDING_OVERLAY`. Anything
 * other than the literal string `'true'` keeps the component inert.
 *
 * Default for ALL deploys is unset → false. This PR does NOT enable the
 * flag in any workflow / variable / env file. Enabling it is a separate
 * `gh variable set` operation on staging.
 */
function isOverlayEnabledByFlag(): boolean {
  // import.meta.env is the Vite build-time env. Reading it here means the
  // gate is evaluated once per build, not per render — but we still call
  // it inline so the flag's value is part of the component's source and
  // the source-level test can grep for it.
  return import.meta.env.VITE_ONBOARDING_OVERLAY === 'true';
}

export default function OnboardingOverlay() {
  // Earliest possible early-return. When flag is OFF, no hook runs, no
  // RTK Query subscription is created, no Modal mounts, no DOM is added.
  // Test #1 + #2 in the companion test file lock this down.
  if (!isOverlayEnabledByFlag()) {
    return null;
  }

  const { isOpen, close } = useOnboardingOverlay();
  if (!isOpen) {
    return null;
  }

  // H-2C2 — onComplete / onSkip / onDemoCreated all close the overlay.
  //
  //   onComplete (RP-path successful create): Fix A persist refetches
  //   state.isCompleted=true → hook predicate flips → overlay stays
  //   closed for the session. Legacy-central + pure-legacy paths
  //   continue rendering the success screen with their explicit CTAs
  //   (those call navigate from inside Steps and unmount the overlay via
  //   route change — out of overlay's hands by design).
  //
  //   onSkip (H-2C2 Phase 2.C — explicit "Skip for now" CTA inside
  //   Steps): UI dismiss only. Sets dismissedThisSession=true; does NOT
  //   mutate server state; fresh page load re-opens overlay if predicate
  //   still matches.
  //
  //   onDemoCreated (H-2C2 Phase 2.C — closes overlay after demo
  //   workspace creation): replaces Steps' default `navigate('/')` so
  //   the overlay closes cleanly even when the user was already at `/`
  //   (the edge case documented in the Step 2.B plan).
  return (
    <Modal
      isOpen={isOpen}
      onClose={close}
      size="xl"
      title="Set up your workspace"
      closeOnOverlayClick={false}
      closeOnEscape={true}
      showCloseButton={true}
    >
      <OnboardingWizardSteps
        onComplete={close}
        onSkip={close}
        onDemoCreated={close}
      />
    </Modal>
  );
}
